import type { StaffRouter } from '@eklevo/api/staff-trpc'
import type { TRPCClient } from '@trpc/client'
import type { EditorAdapter, SaveResult } from '../../components/editor/types'
import { keepaliveMutation } from '../../lib/keepalive'
import type { UploadApi } from '../../lib/uploads'
import type { Reauth } from '../components/reauth'
import { recordActivity } from './activity'
import { touchedRecently } from './passkeys'
import { STAFF_API } from './trpc'

/*
 * The shared course editor's adapter in the back office (ADR 0009 §1): Eklevo's catalogue,
 * through the catalogue's editor operations (`catalog.open`, `catalog.save`, `catalog.publishCheck`,
 * `catalog.markPreviewed`, `catalog.publish`, `catalog.discard`). Which courses it writes is the
 * server's, from the staff session's platform scope. The save on the way out goes with fetch's
 * keepalive to the same operation, as the API client would send it. Publishing asks for the
 * passkey again first when it was not touched in the last minutes (ADR 0008 §4), with the dialog
 * of the other sensitive actions, which says what it confirms; the editor's confirmation says
 * it will ask only then (`asksPasskey`, the reauthentication's own test). Files are uploaded with
 * the back office's own uploads (`uploads.*`, ADR 0009 §6).
 */

export function catalogEditorAdapter(
  client: TRPCClient<StaffRouter>,
  ask: {
    reauth: Pick<Reauth, 'run'>
    /** What the passkey confirms, already translated: publishing the course of that title. */
    purpose: (title: string) => string
  },
): EditorAdapter {
  return {
    scope: 'catalog',
    open: (courseId) => client.catalog.open.mutate({ courseId }),
    save: (input) => client.catalog.save.mutate(input),
    saveKeepalive: (input) =>
      keepaliveMutation<SaveResult>(`${STAFF_API}/catalog.save`, input)?.then((result) => {
        // Answered while the page is still there: it counts as activity, as any other call.
        recordActivity()
        return result
      }) ?? null,
    checkPublish: (input) => client.catalog.publishCheck.query(input),
    markPreviewed: (input) => client.catalog.markPreviewed.mutate(input),
    publish: async (input, { opener, title }) =>
      (await ask.reauth.run(() => client.catalog.publish.mutate(input), {
        from: opener,
        purpose: ask.purpose(title),
      })) ?? null,
    asksPasskey: () => !touchedRecently(),
    discard: (courseId) => client.catalog.discard.mutate({ courseId }),
    uploads: staffUploads(client),
  }
}

/**
 * The back office's uploads (ADR 0009 §6): the catalogue editor's files and the wizard's logo,
 * under uploads/staff/ of the imports bucket. `record` copies a clean file of the catalogue into
 * the platform's files; a logo waits for «Crear empresa».
 */
export function staffUploads(client: TRPCClient<StaffRouter>) {
  return {
    start: (input: Parameters<UploadApi['start']>[0]) => client.uploads.start.mutate(input),
    parts: (input: Parameters<UploadApi['parts']>[0]) => client.uploads.parts.mutate(input),
    finish: (input: Parameters<UploadApi['finish']>[0]) => client.uploads.finish.mutate(input),
    check: (input: Parameters<UploadApi['check']>[0]) => client.uploads.check.mutate(input),
    abort: (input: Parameters<UploadApi['abort']>[0]) => client.uploads.abort.mutate(input),
    record: (input: { token: string }) => client.uploads.record.mutate(input),
  } satisfies UploadApi & { record: unknown }
}
